'use client';

import type { CompanyCountryCode } from '@/lib/countries';
import { memo, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { AlertTriangle, Euro, FileText, Gavel, Lock, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CompanyHistory, InsolvencyRecordItem, TenderItem } from '@/lib/backend-api';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SourceAttribution } from './SourceAttribution';
import { BoardEventsTable, CapitalEventsTable, CompanyEventsTable, InsolvencyEventsTable, TendersTable } from './CompanyHistoryTables';
import { buildBoardEvents, buildCapitalEvents, buildCompanyEvents, buildInsolvencyEvents } from './companyHistoryEvents';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const PREVIEW_ROWS = 2;

export const CompanyHistoryCard = memo(function CompanyHistoryCard({
  history,
  isAuthenticated = true,
  insolvencyRecords = [],
  tenders = [],
  cpvProducts = [],
  tendersTotal = 0,
  euId,
  showRegisterAttribution = false,
  country,
  registerCourt,
  registerId,
  capturedAt,
  className,
}: {
  /**
   * The CD-derived timeline, or `null` when the company has no CD event
   * aggregate — which is 38% of the companies that have tenders. The card
   * still mounts for those: the Vergabe tab is the only content it has, and
   * hiding it would lose the data entirely.
   */
  history: CompanyHistory | null;
  isAuthenticated?: boolean;
  euId?: string;
  insolvencyRecords?: InsolvencyRecordItem[];
  /** TED notices, newest first, already deduplicated server-side. */
  tenders?: TenderItem[];
  /** CPV category descriptions across those notices — what the company buys or sells. */
  cpvProducts?: string[];
  /**
   * Notices this company appears in, counted before the server's page limit
   * and its per-role dedup. `tenders.length` is a different number and must
   * not be shown in its place.
   */
  tendersTotal?: number;
  showRegisterAttribution?: boolean;
  /** Picks the register credited by the attribution: the Firmenbuch for AT,
   *  else the German Handelsregister (callers hide it for CH). */
  country?: CompanyCountryCode;
  registerCourt?: string | null;
  registerId?: string | null;
  capturedAt?: string | null;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { locale } = useLocale();

  const companyEvents = useMemo(
    () => (history ? buildCompanyEvents(history, t) : []),
    [history, t],
  );
  const capitalEvents = useMemo(
    () => (history ? buildCapitalEvents(history, t) : []),
    [history, t],
  );
  const boardEvents = useMemo(() => {
    const events = history ? buildBoardEvents(history, t) : [];
    if (!isAuthenticated) return events.map((e) => ({ ...e, age: null }));
    return events;
  }, [history, t, isAuthenticated]);
  const insolvencyEvents = useMemo(
    () => buildInsolvencyEvents(insolvencyRecords),
    [insolvencyRecords],
  );

  const hasInsolvencyTab = insolvencyEvents.length > 0;
  const hasTendersTab = tenders.length > 0;
  const tabCount = 3 + (hasInsolvencyTab ? 1 : 0) + (hasTendersTab ? 1 : 0);

  const defaultTab =
    companyEvents.length > 0
      ? 'company'
      : capitalEvents.length > 0
        ? 'capital'
        : isAuthenticated && boardEvents.length > 0
          ? 'board'
          : hasTendersTab
            ? 'tenders'
            : 'company';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const totalEvents =
    companyEvents.length +
    capitalEvents.length +
    (isAuthenticated ? boardEvents.length : 0) +
    (isAuthenticated && hasInsolvencyTab ? insolvencyEvents.length : 0) +
    // Guests see a blurred preview rather than nothing, so tenders count
    // toward "is there anything here" for them too — unlike the board tab,
    // which is replaced by a lock.
    tendersTotal;
  if (totalEvents === 0 && (!boardEvents.length || isAuthenticated))
    return null;

  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <div className="flex items-start gap-2 w-full">
          <div className="flex-1">
            <CardTitle as="h2">
              {t('companyDetail.companyHistory', 'Unternehmenshistorie')}
            </CardTitle>
          </div>
          <Badge variant="outline" className="shrink-0 mt-1">
            {totalEvents}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-5 pt-2">
            <TabsList
              variant="line"
              size="sm"
              className={cn(
                'w-full grid grid-cols-2',
                tabCount === 5
                  ? 'sm:grid-cols-5'
                  : tabCount === 4
                    ? 'sm:grid-cols-4'
                    : 'sm:grid-cols-3',
                !isAuthenticated ? 'mb-1' : 'mb-3',
              )}
            >
              <TabsTrigger
                value="company"
                className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                disabled={companyEvents.length === 0}
              >
                <FileText size={13} className="shrink-0" />
                <span className="truncate">
                  {t('companyHistory.tabCompany', 'Company')}
                </span>
                {companyEvents.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {companyEvents.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="capital"
                className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                disabled={capitalEvents.length === 0}
              >
                <Euro size={13} className="shrink-0" />
                <span className="truncate">
                  {t('companyHistory.tabCapital', 'Capital')}
                </span>
                {capitalEvents.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {capitalEvents.length}
                  </Badge>
                )}
              </TabsTrigger>
              {isAuthenticated ? (
                <TabsTrigger
                  value="board"
                  className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                  disabled={boardEvents.length === 0}
                >
                  <Users size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyHistory.tabBoard', 'Management')}
                  </span>
                  {boardEvents.length > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                    >
                      {boardEvents.length}
                    </Badge>
                  )}
                </TabsTrigger>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground cursor-not-allowed px-2 py-1.5 min-h-[44px] sm:min-h-0">
                        <Lock size={12} className="shrink-0" />
                        <span className="truncate">
                          {t('companyHistory.tabBoard', 'Management')}
                        </span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(
                        'companyHistory.loginToSeeBoard',
                        'Kostenlos anmelden um Geschäftsführung zu sehen',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {hasInsolvencyTab &&
                (isAuthenticated ? (
                  <TabsTrigger
                    value="insolvency"
                    className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                  >
                    <AlertTriangle size={13} className="shrink-0" />
                    <span className="truncate">
                      {t('companyHistory.tabInsolvency', 'Insolvenz & Liquidation')}
                    </span>
                    <Badge
                      variant="secondary"
                      className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                    >
                      {insolvencyEvents.length}
                    </Badge>
                  </TabsTrigger>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground cursor-not-allowed px-2 py-1.5 min-h-[44px] sm:min-h-0">
                          <Lock size={12} className="shrink-0" />
                          <span className="truncate">
                            {t('companyHistory.tabInsolvency', 'Insolvenz & Liquidation')}
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(
                          'companyHistory.loginToSeeInsolvency',
                          'Kostenlos anmelden um Insolvenz- und Liquidationsverfahren zu sehen',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              {/* Vergabe is shown to guests as a blurred preview rather than
                  a lock: unlike board members, the first row is already
                  public on TED, so a teaser is honest and converts better
                  than a padlock. */}
              {hasTendersTab && (
                <TabsTrigger
                  value="tenders"
                  className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                >
                  <Gavel size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyHistory.tabTenders', 'Vergabe')}
                  </span>
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {tendersTotal || tenders.length}
                  </Badge>
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <div>
            {/* forceMount keeps every panel in the SSR HTML so the register /
                capital event tables are crawlable even when another tab is
                active. Radix doesn't hide force-mounted panels itself, so
                inactive ones are hidden via data-[state=inactive]:hidden. */}
            <TabsContent
              value="company"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <CompanyEventsTable
                events={
                  !isAuthenticated ? companyEvents.slice(0, 3) : companyEvents
                }
                t={t}
                blurAfter={!isAuthenticated ? PREVIEW_ROWS : undefined}
              />
              {!isAuthenticated && companyEvents.length > 2 && (
                <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      trackSignupAndRedirect(pathname, 'company_history', euId, 'login');
                    }}
                    className="text-primary hover:underline"
                  >
                    {t(
                      'companyHistory.registerToSeeAll',
                      'Sign in to see all {{count}} events',
                      { count: companyEvents.length },
                    )}
                  </a>
                </div>
              )}
            </TabsContent>
            <TabsContent
              value="capital"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <CapitalEventsTable
                events={
                  !isAuthenticated ? capitalEvents.slice(0, 3) : capitalEvents
                }
                t={t}
                blurAfter={!isAuthenticated ? PREVIEW_ROWS : undefined}
              />
              {!isAuthenticated && capitalEvents.length > 2 && (
                <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      trackSignupAndRedirect(pathname, 'company_history', euId, 'login');
                    }}
                    className="text-primary hover:underline"
                  >
                    {t(
                      'companyHistory.registerToSeeAllCapital',
                      'Sign in to see all {{count}} capital events',
                      { count: capitalEvents.length },
                    )}
                  </a>
                </div>
              )}
            </TabsContent>
            {isAuthenticated && (
              <TabsContent
                value="board"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <BoardEventsTable
                  events={boardEvents}
                  t={t}
                  isAuthenticated={isAuthenticated}
                />
              </TabsContent>
            )}
            {isAuthenticated && hasInsolvencyTab && (
              <TabsContent
                value="insolvency"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <InsolvencyEventsTable events={insolvencyEvents} t={t} />
              </TabsContent>
            )}
            {hasTendersTab && (
              <TabsContent
                value="tenders"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                {cpvProducts.length > 0 && (
                  <div className="px-5 py-3 border-b border-border/40">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      {t('companyHistory.cpvCategories', 'CPV-Kategorien')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cpvProducts.slice(0, 12).map((p) => (
                        <Badge
                          key={p}
                          variant="secondary"
                          className="text-[10px]"
                        >
                          {p}
                        </Badge>
                      ))}
                      {cpvProducts.length > 12 && (
                        <Badge variant="outline" className="text-[10px]">
                          +{cpvProducts.length - 12}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                <TendersTable
                  tenders={!isAuthenticated ? tenders.slice(0, 3) : tenders}
                  t={t}
                  locale={locale}
                  blurAfter={!isAuthenticated ? PREVIEW_ROWS : undefined}
                />
                {!isAuthenticated && tendersTotal > PREVIEW_ROWS && (
                  <div
                    className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center"
                    data-nosnippet
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        trackSignupAndRedirect(pathname, 'company_tenders', euId, 'login');
                      }}
                      className="text-primary hover:underline"
                    >
                      {t(
                        'companyHistory.registerToSeeAllTenders',
                        'Sign in to see all {{count}} tenders',
                        { count: tendersTotal },
                      )}
                    </a>
                  </div>
                )}
                {/* The server pages tenders; say so rather than letting the
                    badge's count and the row count silently disagree. */}
                {isAuthenticated && tendersTotal > tenders.length && (
                  <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
                    {t(
                      'companyHistory.tendersTruncated',
                      'Showing the {{shown}} most recent of {{total}} notices',
                      { shown: tenders.length, total: tendersTotal },
                    )}
                  </div>
                )}
              </TabsContent>
            )}
          </div>
        </Tabs>
        {showRegisterAttribution && history && (
          <SourceAttribution
            source={country === 'AT' ? 'firmenbuch' : 'handelsregister'}
            registerCourt={registerCourt}
            registerId={registerId}
            capturedAt={capturedAt}
            className="mx-5"
          />
        )}
      </CardContent>
    </Card>
  );
});
